/**
 * Search Presentation Tool Schema
 * 
 * Schema for searching within PowerPoint presentations using
 * various search types: semantic, exact, fuzzy, visual.
 */

import { z } from "zod";

// ═══════════════════════════════════════════════════════════════════════════
// SEARCH TYPE
// ═══════════════════════════════════════════════════════════════════════════

export const SearchTypeSchema = z.enum(["semantic", "exact", "fuzzy", "visual"])
    .describe("Type of search: 'semantic' (meaning-based), 'exact' (exact text match), 'fuzzy' (approximate match), 'visual' (image similarity)");

export type SearchType = z.infer<typeof SearchTypeSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// RESULT TYPES
// ═══════════════════════════════════════════════════════════════════════════

export const ResultTypeSchema = z.enum(["slide", "component", "asset", "shape", "layout"])
    .describe("Type of result to return");

export type ResultType = z.infer<typeof ResultTypeSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// VISUAL SOURCE
// ═══════════════════════════════════════════════════════════════════════════

export const VisualSourceSchema = z.object({
    slideId: z.string().optional()
        .describe("Slide ID to use as visual search source"),
    slideIndex: z.number().optional()
        .describe("Slide index (0-based) to use as visual search source"),
    componentId: z.string().optional()
        .describe("Component ID to use as visual search source"),
    shapeIds: z.array(z.string()).optional()
        .describe("Shape IDs to crop and use as visual search source (requires slideId or slideIndex)"),
}).describe("Source for visual similarity search");

export type VisualSource = z.infer<typeof VisualSourceSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// MAIN SEARCH PARAMS SCHEMA
// ═══════════════════════════════════════════════════════════════════════════

export const SearchPresentationParamsSchema = z.object({
    uiDescription: z.string()
        .min(1, "uiDescription is required")
        .describe("Single-line UI status text shown in the tool card (e.g., 'Searching for revenue slides')."),

    // Search scope (single deck, multi deck, or ALL)
    documentId: z.union([z.string(), z.number()])
        .optional()
        .describe("Single deck selector. Use index (0, 1), 'CURRENT', explicit deck ID, or 'ALL' for global backend indexed library."),

    documentIds: z.array(z.union([z.string(), z.number()]))
        .optional()
        .describe("Multiple deck selectors. Array of indexes (0, 1), 'CURRENT', or explicit deck IDs."),
    
    searchType: SearchTypeSchema,
    
    resultTypes: z.array(ResultTypeSchema)
        .min(1, "At least one result type is required")
        .describe("Types of results to return: 'slide', 'component', 'asset', 'shape', 'layout'"),
    
    // Text search fields (for semantic, exact, fuzzy)
    query: z.string().optional()
        .describe("Search query for text-based searches (semantic, exact, fuzzy)"),
    
    // Visual search fields
    visualSource: VisualSourceSchema.optional()
        .describe("Source for visual similarity search (required when searchType='visual')"),
    
    // Inclusion filters
    slidesToInclude: z.array(z.union([z.string(), z.number()])).optional()
        .describe("Limit search to these slides only. Can be slide IDs or indexes."),
    
    // Exclusion filters  
    slidesToExclude: z.array(z.union([z.string(), z.number()])).optional()
        .describe("Exclude these slides from results"),
    
    componentsToExclude: z.array(z.string()).optional()
        .describe("Exclude these component IDs from results"),
    
    assetsToExclude: z.array(z.string()).optional()
        .describe("Exclude these asset IDs from results"),
    
    shapesToExclude: z.array(z.string()).optional()
        .describe("Exclude results containing these shape IDs"),
    
    layoutsToExclude: z.array(z.string()).optional()
        .describe("Exclude these layout IDs from results"),
    
    // Exclude previous results flag
    excludeAllPrevious: z.boolean().optional().default(false)
        .describe("Automatically exclude all results from previous searches in this conversation. Set to true to avoid duplicate results."),
    
    // Result limit
    numResults: z.number()
        .min(1)
        .max(50)
        .default(10)
        .optional()
        .describe("Maximum number of results to return (1-50, default: 10)"),
}).superRefine((params, ctx) => {
    if (params.documentId !== undefined && params.documentIds !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['documentIds'],
            message: "Provide either documentId or documentIds, not both.",
        });
    }

    if (Array.isArray(params.documentIds) && params.documentIds.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['documentIds'],
            message: 'documentIds must contain at least one deck selector when provided.',
        });
    }
});

export type SearchPresentationParams = z.infer<typeof SearchPresentationParamsSchema>;




